import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import { Plus, Search, Eye, Edit, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import { Employee, EmployeeListResponse } from '@/api/types'

/**
 * Employees List Page
 * Displays a table of employees with CRUD operations
 */
export const EmployeesList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const branchId = user?.selectedBranch?.branchId

  // Fetch employees
  useEffect(() => {
    if (!branchId) {
      toast.error('Please select a branch first')
      return
    }

    const fetchEmployees = async () => {
      try {
        setLoading(true)
        const response: EmployeeListResponse = await employeesService.getAllEmployees(
          branchId,
          pageNumber,
          pageSize
        )
        setEmployees(response.data || [])
        setTotalCount(response.totalRecords || 0)
        // Calculate total pages
        const totalPages = response.totalRecords 
          ? Math.ceil(response.totalRecords / response.pageSize) 
          : 0
        setTotalPages(totalPages)
      } catch (error: any) {
        console.error('Error fetching employees:', error)
        toast.error(error.response?.data?.message || 'Failed to fetch employees')
      } finally {
        setLoading(false)
      }
    }

    fetchEmployees()
  }, [branchId, pageNumber, pageSize])

  // Client-side search filtering
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) {
      return employees
    }

    const query = searchQuery.toLowerCase()
    return employees.filter((emp) => {
      const fullNameEn = `${emp.empFirstNameEn || ''} ${emp.empLastNameEn || ''}`.toLowerCase()
      const fullNameAr = `${emp.empFirstNameAr || ''} ${emp.empLastNameAr || ''}`.toLowerCase()
      const empCode = (emp.empCode || '').toLowerCase()
      const email = (emp.empEmail || '').toLowerCase()
      const mobile = (emp.empMobileNo || '').toLowerCase()

      return (
        fullNameEn.includes(query) ||
        fullNameAr.includes(query) ||
        empCode.includes(query) ||
        email.includes(query) ||
        mobile.includes(query)
      )
    })
  }, [employees, searchQuery])

  const handleAddEmployee = () => {
    navigate('/employees/new')
  }

  const handleViewEmployee = (id: number) => {
    navigate(`/employees/${id}/view`)
  }

  const handleEditEmployee = (id: number) => {
    navigate(`/employees/${id}/edit`)
  }

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1) // Reset to first page when page size changes
  }

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1)
    }
  }

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1)
    }
  }

  const getEmployeeFullName = (emp: Employee) => {
    const firstName = emp.empFirstNameEn || emp.empFirstNameAr || ''
    const lastName = emp.empLastNameEn || emp.empLastNameAr || ''
    return `${firstName} ${lastName}`.trim() || 'N/A'
  }

  const getEmployeeInitials = (emp: Employee) => {
    const firstName = emp.empFirstNameEn || emp.empFirstNameAr || ''
    const lastName = emp.empLastNameEn || emp.empLastNameAr || ''
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'NA'
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('navigation.employees')}
          description="Manage your organization's employees"
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title="Branch Not Selected"
              description="Please select a branch to view employees."
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.employees')}
        description="Manage your organization's employees"
        action={{
          label: 'Add New Employee',
          onClick: handleAddEmployee,
          icon: Plus,
        }}
      />

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search employees by name, code, email, or mobile..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading employees...</p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery ? "No employees found" : "No employees found"}
                description={
                  searchQuery
                    ? "Try adjusting your search query"
                    : "Get started by adding your first employee to the system."
                }
                action={
                  !searchQuery
                    ? {
                        label: 'Add New Employee',
                        onClick: handleAddEmployee,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className={`w-[60px] h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                      {t('employees.photo') || 'Photo'}
                    </TableHead>
                    <TableHead className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                      {t('employees.employeeCode') || 'Employee Code'}
                    </TableHead>
                    <TableHead className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                      {t('employees.fullName') || 'Full Name'}
                    </TableHead>
                    <TableHead className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                      {t('employees.email') || 'Email'}
                    </TableHead>
                    <TableHead className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                      {t('employees.mobile') || 'Mobile'}
                    </TableHead>
                    <TableHead className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                      {t('employees.department') || 'Department'}
                    </TableHead>
                    <TableHead className={`h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                      {t('employees.status') || 'Status'}
                    </TableHead>
                    <TableHead className={`h-12 px-3 text-right bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'pr-6' : 'pl-6'}`}>
                      {t('employees.actions') || 'Actions'}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow 
                      key={employee.id} 
                      className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors cursor-pointer"
                    >
                      <TableCell className="px-3 py-2">
                        <Avatar className="h-9 w-9">
                          <AvatarImage
                            src={employee.imageUrl || employee.profileImageUrl}
                            alt={getEmployeeFullName(employee)}
                          />
                          <AvatarFallback className="text-xs">
                            {getEmployeeInitials(employee)}
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className={`px-3 py-2 font-medium text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                        {employee.empCode || 'N/A'}
                      </TableCell>
                      <TableCell className={`px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>{getEmployeeFullName(employee)}</TableCell>
                      <TableCell className={`px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>{employee.empEmail || 'N/A'}</TableCell>
                      <TableCell className={`px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>{employee.empMobileNo || 'N/A'}</TableCell>
                      <TableCell className={`px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>{employee.departmentName || 'N/A'}</TableCell>
                      <TableCell className="px-3 py-2">
                        <Badge
                          variant={employee.isActive === 1 ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {employee.isActive === 1 ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewEmployee(employee.id!)}
                            className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditEmployee(employee.id!)}
                            className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="border-t px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {searchQuery ? (
                      <span>
                        Showing {filteredEmployees.length} of {totalCount} employees
                        {searchQuery && ` (filtered from ${totalCount} total)`}
                      </span>
                    ) : (
                      <span>
                        Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                        {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} employees
                      </span>
                    )}
                  </div>
                  {!searchQuery && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousPage}
                        disabled={pageNumber === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <div className="text-sm">
                        Page {pageNumber} of {totalPages || 1}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={pageNumber >= totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
